import React, { forwardRef, useImperativeHandle } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';
import numeral from 'numeral';
import 'numeral/locales/pt-br';

interface GeneralSummaryCardProps {
  data: any;
  loading: boolean;
  onRefresh: () => void;
}

export interface GeneralSummaryCardRef {
  refresh: () => void;
}

const GeneralSummaryCard = forwardRef<GeneralSummaryCardRef, GeneralSummaryCardProps>(
  ({ data, loading, onRefresh }, ref) => {
    useImperativeHandle(ref, () => ({
      refresh: onRefresh
    }));

    if (loading) {
      return <SkeletonCard rows={1} columns={6} />;
    }

    if (!data || !data.total) {
      return (
        <Card className={styles.reportCard}>
          <CardContent>
            <div className="text-center text-muted">
              <i className="ri-information-line me-2"></i>
              Nenhum dado disponível
            </div>
          </CardContent>
        </Card>
      );
    }

    // Calculate remaining impressions based on pricing model
    const calculateRemaining = () => {
      if (!data.total) return 0;
      
      const contracted = data.total.purchaseImpressions || 0;
      const delivered = data.total.impression || 0;
      const viewable = data.total.viewable || 0;
      const clicks = data.total.click || 0;
      
      // For general summary, we'll use impression as the base calculation
      // In a real scenario, you might want to calculate based on the dominant pricing model
      return contracted - delivered;
    };

    const numeralInteger = '0,0';
    const numeralDecimalFormat = '0.00';

    return (
      <Card className={styles.reportCard}>
        <CardContent className="p-0">
          <div className="grid grid-cols-6 gap-0">
            <div className={styles.metricCard}>
              <div className={styles.metricLabel}>Contratado</div>
              <div className={styles.metricValue}>
                {numeral(data.total?.purchaseImpressions || 0).format(numeralInteger)}
              </div>
            </div>
            <div className={styles.metricCard}>
              <div className={styles.metricLabel}>Entregues</div>
              <div className={styles.metricValue}>
                {numeral(data.total?.impression || 0).format(numeralInteger)}
              </div>
            </div>
            <div className={styles.metricCard}>
              <div className={styles.metricLabel}>Restantes</div>
              <div className={styles.metricValue}>
                {numeral(calculateRemaining()).format(numeralInteger)}
              </div>
            </div>
            <div className={styles.metricCard}>
              <div className={styles.metricLabel}>Visualizadas</div>
              <div className={styles.metricValue}>
                {numeral(data.total?.viewable || 0).format(numeralInteger)}
              </div>
            </div>
            <div className={styles.metricCard}>
              <div className={styles.metricLabel}>Visibilidade</div>
              <div className={styles.metricValue}>
                {data.total?.impression && data.total?.viewable
                  ? `${numeral((data.total.viewable / data.total.impression) * 100).format(numeralDecimalFormat)}%`
                  : '0%'
                }
              </div>
            </div>
            <div className={styles.metricCard}>
              <div className={styles.metricLabel}>Entrega</div>
              <div className={styles.metricValue}>
                {data.total?.purchaseImpressions && data.total?.impression
                  ? `${numeral((data.total.impression / data.total.purchaseImpressions) * 100).format(numeralDecimalFormat)}%`
                  : '0%'
                }
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
);

GeneralSummaryCard.displayName = 'GeneralSummaryCard';

export default GeneralSummaryCard;
